import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/elephant/constants';
import { RANGE_FILTER_SEPARATOR } from '@aibees/elephant/constants/filters';
import { useTrackEvents } from '@aibees/elephant/hooks';
import { useSearchQueryFilter } from '@aibees/elephant/hooks/use-search-query-filter';
import {
  NumberRangeInput,
  NumberRangeInputTuple,
  NumberRangeInputValue,
  NumberRangeSelect,
} from '@aibees/shared/components';
import { useDebounceFn } from '@aibees/shared/hooks';
import { classnames, peformanceLogTime } from '@aibees/shared/utils';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { PresentSwitchFilter } from '../../components/present-switch-filter';
import { usePresentChecked } from '../../components/present-switch-filter/use-present-checked';
import { SEARCH_DELAY } from '../../constants';
import { useRangeValueFromSearchUrl } from '../../hooks/use-range-value-from-search-url';
import type { NumberRangeFilterProps as Props } from './types';
import { buildDraft } from './utils';

export const NumberRangeFilter = memo(
  ({
    className,
    field,
    name,
    nested,
    open,
    metadata,
    options,
    type,
    onChange,
    value,
    error,
  }: Props) => {
    const { setParams, params } = useSearchQueryFilter({
      select: [field!],
    });
    const { track } = useTrackEvents();
    const checked = usePresentChecked(field!);
    // When nested (onChange provided), data lives in parent's field; use only
    // value so we never read params[field!] which is a different store key.
    const val = useRangeValueFromSearchUrl<NumberRangeInputTuple>(
      onChange ? value ?? '' : value ?? params[field!],
      type
    );

    // When used as nested controlled input (e.g. employee-by-department), call
    // onChange immediately so the parent store updates and the value flows back.
    // Otherwise debounce to avoid excessive URL/store updates while typing.
    const applyUpdate = useCallback(
      (startValue: NumberRangeInputValue, endValue: NumberRangeInputValue) => {
        if (LOG_API_CALL_DURATION_ENABLE)
          peformanceLogTime(`filter change start: ${field}`);

        const currentValString = [startValue, endValue].join(
          RANGE_FILTER_SEPARATOR
        );

        const draft = buildDraft(
          params,
          type,
          field!,
          'add',
          0,
          startValue,
          currentValString || undefined
        );

        if (onChange) {
          onChange?.(draft);
        } else {
          setParams({ [field!]: draft });
          track('FILTER', {
            filterName: field,
            filterType: type,
            filterValue: { start: startValue, end: endValue },
            range: draft,
            searchType: type,
          });
        }

        if (LOG_API_CALL_DURATION_ENABLE)
          peformanceLogTime(`filter change end: ${field}`);
      },
      [params, type, field, onChange, setParams, track]
    );

    const debouncedUpdate = useDebounceFn(applyUpdate, SEARCH_DELAY);

    const handleChangeRange = useCallback(
      (startValue: NumberRangeInputValue, endValue: NumberRangeInputValue) => {
        if (onChange) {
          applyUpdate(startValue, endValue);
        } else {
          debouncedUpdate(startValue, endValue);
        }
      },
      [onChange, applyUpdate, debouncedUpdate]
    );

    if (!open) return null;

    const isPercent = metadata?.isPercent;

    const content = (
      <>
        {options ? (
          <NumberRangeSelect
            min={0}
            icon={metadata?.icon ?? undefined}
            onChangeRange={handleChangeRange}
            metadata={metadata}
            value={val}
            disabled={checked}
            error={error}
            options={options ?? []}
          />
        ) : (
          <NumberRangeInput
            min={metadata?.numberRange?.min ?? 0}
            max={metadata?.numberRange?.max}
            icon={metadata?.icon ?? undefined}
            onChangeRange={handleChangeRange}
            value={val}
            error={error}
            isPercent={isPercent}
            acceptNegative={metadata?.numberRange?.acceptNegative}
          />
        )}
      </>
    );

    if (nested)
      return (
        <Box
          display="flex"
          flexDirection="column"
          gap={0.5}
          sx={metadata?.styles?.sx}
          className={classnames('NumberRangeFilter', className)}
        >
          {!metadata?.numberRange?.hideTitle && (
            <Typography variant="body4" color="grey.500">
              {name}
            </Typography>
          )}
          {content}
        </Box>
      );

    return (
      <Card
        className={classnames('NumberRangeFilter', className)}
        variant="filled"
        color="default"
        sx={metadata?.styles?.sx}
      >
        {!!name && (
          <CardHeader
            padding="normal"
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography variant="body3" color="grey.600" fontWeight={500}>
                  {name}
                </Typography>
                {metadata?.numberRange?.switch && (
                  <PresentSwitchFilter field={field!} metadata={metadata} />
                )}
              </Box>
            }
          />
        )}
        <CardContent padding="normal">{content}</CardContent>
      </Card>
    );
  }
);

NumberRangeFilter.displayName = 'NumberRangeFilter';
